import { Link } from "react-router";
import { formatInr } from "@/lib/format/inr";
import { formatRelativeTime } from "@/lib/format/relative-time";
import type { ExpenseDetail } from "@/lib/expenses/expense-detail";
import { CategoryIcon } from "@/components/category-icon/category-icon";
import { formatAdminDateTime, formatCalendarDate } from "@/lib/format/admin-time";

export interface ExpenseDetailViewProps {
  expense: ExpenseDetail;
  now: Date;
}

function Chip({
  tone = "neutral",
  children,
}: {
  tone?: "neutral" | "good" | "warn" | "info" | "bad";
  children: React.ReactNode;
}) {
  const cls = {
    neutral: "text-fc-text-secondary border-fc-border bg-fc-overlay",
    good: "text-fc-brand border-fc-brand/40 bg-fc-brand/10",
    warn: "text-fc-warn border-fc-warn/40 bg-fc-warn/10",
    info: "text-fc-info border-fc-info/40 bg-fc-info/10",
    bad: "text-fc-bad border-fc-bad/40 bg-fc-bad/10",
  }[tone];
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${cls}`}
    >
      {children}
    </span>
  );
}

// Moments: admins' clock (IST), labelled.
const toDateTime = formatAdminDateTime;

// Expense.date is a calendar date: shown as stored.
const toDate = formatCalendarDate;

function PropRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <div className="pt-0.5 text-[10.5px] uppercase tracking-wide text-fc-text-dim">
        {label}
      </div>
      <div className="text-[12.5px] text-fc-text">{children}</div>
    </>
  );
}

export function ExpenseDetailView({ expense, now }: ExpenseDetailViewProps) {
  const isDeleted = expense.deletedAt != null;

  return (
    <div className="flex flex-col gap-6">
      {isDeleted ? (
        <section className="rounded-xl border border-fc-bad/40 bg-fc-bad/10 p-4">
          <div className="text-[11px] uppercase tracking-wide text-fc-bad">
            Deleted
          </div>
          <div className="mt-1 text-[12.5px] text-fc-text">
            Removed by{" "}
            <span className="font-medium">
              {expense.deleterName ?? "—"}
            </span>
            {expense.deletedAt
              ? ` · ${formatRelativeTime(expense.deletedAt, now)}`
              : ""}
          </div>
        </section>
      ) : null}

      {/* HERO */}
      <section className="flex items-center gap-4">
        <CategoryIcon emoji={expense.categoryEmoji} size="xl" />
        <div className="min-w-0">
          <h1 className="font-mono text-[28px] font-semibold tracking-tight text-fc-text">
            {formatInr(expense.amount)}
          </h1>
          <div className="text-[13px] text-fc-text-dim">
            {expense.note ?? "—"}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Chip tone="info">
              <Link to={`/admin/groups/${expense.groupId}`}>
                {expense.groupName}
              </Link>
            </Chip>
            <Chip>{expense.categoryName}</Chip>
            {expense.paymentMethodName ? (
              <Chip>{expense.paymentMethodName}</Chip>
            ) : null}
            {expense.recurringExpenseId ? (
              <Chip tone="warn">recurring</Chip>
            ) : null}
            <span className="font-mono text-[10.5px] tracking-wider text-fc-text-dim">
              e_{expense.id.slice(0, 8)}
            </span>
          </div>
        </div>
      </section>

      {/* PROP GRID */}
      <section className="rounded-2xl border border-fc-border bg-gradient-to-b from-fc-raised to-fc-overlay p-5">
        <div className="mb-4">
          <div className="text-[13px] font-semibold text-fc-text">
            Expense properties
          </div>
          <div className="text-[11.5px] text-fc-text-dim">
            Fields from schema
          </div>
        </div>
        <div className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2.5">
          <PropRow label="Expense ID">
            <span className="rounded-md border border-fc-border bg-fc-overlay px-2 py-0.5 font-mono text-[11.5px]">
              {expense.id}
            </span>
          </PropRow>

          <PropRow label="Amount">
            <span className="font-mono font-semibold">
              {formatInr(expense.amount)}
            </span>
          </PropRow>

          <PropRow label="Group">
            <Link
              to={`/admin/groups/${expense.groupId}`}
              className="text-fc-text underline decoration-fc-border underline-offset-4 hover:decoration-fc-brand"
            >
              {expense.groupName}
            </Link>
          </PropRow>

          <PropRow label="Category">
            <span className="inline-flex items-center gap-2">
              <CategoryIcon emoji={expense.categoryEmoji} size="sm" />
              {expense.categoryName}
            </span>
          </PropRow>

          <PropRow label="Payer">
            <Link
              to={`/admin/users/${expense.payerId}`}
              className="text-fc-text underline decoration-fc-border underline-offset-4 hover:decoration-fc-brand"
            >
              {expense.payerName}
            </Link>
          </PropRow>

          <PropRow label="Creator">
            {expense.creatorId === expense.payerId ? (
              <span className="text-fc-text-dim">same as payer</span>
            ) : (
              <Link
                to={`/admin/users/${expense.creatorId}`}
                className="text-fc-text underline decoration-fc-border underline-offset-4 hover:decoration-fc-brand"
              >
                {expense.creatorName}
              </Link>
            )}
          </PropRow>

          <PropRow label="Payment method">
            {expense.paymentMethodName ?? "—"}
          </PropRow>

          <PropRow label="Note">{expense.note ?? "—"}</PropRow>

          <PropRow label="Receipt">
            {expense.receiptUrl ? (
              <a
                href={expense.receiptUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-fc-brand underline decoration-fc-brand/60 underline-offset-4 hover:decoration-fc-brand"
              >
                View receipt →
              </a>
            ) : (
              "—"
            )}
          </PropRow>

          <PropRow label="Recurring">
            {expense.recurringExpenseId ? (
              <span className="font-mono text-[11.5px]">
                {expense.recurringExpenseId}
              </span>
            ) : (
              "—"
            )}
          </PropRow>

          <PropRow label="Date">
            <span className="font-mono text-[12px]">
              {toDate(expense.date)}
            </span>
          </PropRow>

          <PropRow label="Created">
            <span className="font-mono text-[12px]">
              {toDateTime(expense.createdAt)}
            </span>
          </PropRow>

          <PropRow label="Updated">
            <span className="font-mono text-[12px]">
              {toDateTime(expense.updatedAt)}
            </span>
          </PropRow>

          {expense.editedAt ? (
            <PropRow label="Edited">
              <span className="font-mono text-[12px]">
                {toDateTime(expense.editedAt)}
              </span>
              {expense.editorName ? ` by ${expense.editorName}` : ""}
            </PropRow>
          ) : null}
        </div>
      </section>
    </div>
  );
}
